import { useQueryClient } from '@tanstack/react-query';
import { Button, Descriptions, Space, Table, Tag } from 'antd';
import {
  orderSideColors,
  orderSideLabels,
  orderTypeColors,
  programOrderStatus,
  programOrderStatusColors,
  programOrderStatusComplete,
} from 'apps/investment/constants/programOrder';
import { DataTable } from 'common/data-table';
import LoadingSpinner from 'common/ui/LoadingSpinner';
import { useFetchApiGet, usePostApi } from 'hooks/api';
import { EyeIcon } from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from 'utils/formatDate';
import { employeeProgramOrdersColumnsGenerator } from './config';

const EstimateVesting = ({
  id,
  companyId,
  employeeId,
  apiCall,
  programStatus,
}) => {
  const queryClient = useQueryClient();

  const { data, isLoading: isLoadingEstimateVesting, load: loadEstimate } = useFetchApiGet(
    apiCall.estimate,
    { resourceName: 'data' }
  );

  // Creating and bundling sell orders writes both the program orders and the
  // fund orders behind them synchronously (CreateNewCompanyProgramSellOrders /
  // BundleProgramSellOrders), so the estimate table and the orders list are
  // both stale once this resolves.
  const refreshSellOrders = () => {
    queryClient.invalidateQueries({ queryKey: [apiCall.estimate.queryKey] });
    queryClient.invalidateQueries({ queryKey: [apiCall.detail.queryKey] });
    queryClient.invalidateQueries({ queryKey: [apiCall.list.queryKey] });
  };

  useEffect(() => {
    if (id) loadEstimate({ id });
  }, [id]);

  const {
    post: postTriggerCompanySellOrder,
    isLoading: isSubmittingTriggerSellOrder,
  } = usePostApi(apiCall.createCompanySellOrder, {
    resourceName: 'data',
    onSuccess: refreshSellOrders,
  });

  const {
    post: postTriggerCompanySellAllOrders,
    isLoading: isSubmittingTriggerCompanySellAllOrders,
  } = usePostApi(apiCall.createCompanySellAllOrders, {
    resourceName: 'data',
    onSuccess: refreshSellOrders,
  });

  const onTriggerCompanySellAllOrders = () => {
    const payload = {
      company_id: companyId,
      employee_program_id: id,
    };
    postTriggerCompanySellAllOrders({ id, payload });
  };

  const onTriggerCompanySellOrder = product_id => {
    const payload = {
      company_id: companyId,
      employee_program_id: id,
      product_id: product_id,
    };
    postTriggerCompanySellOrder({ id, payload });
  };

  if (isLoadingEstimateVesting) {
    return <LoadingSpinner />;
  }

  const { estimate, program_orders = [] } = data || {};
  const employeeProgramOrders = program_orders.filter(
    o => o.order_side == 'employee' && o.order_type == 'buy'
  );

  const companyProgramOrders = program_orders.filter(
    o => o.order_side == 'company'
  );
  const companyProgramOrdersMap = companyProgramOrders.reduce((map, obj) => {
    map[obj.interval] = obj;
    return map;
  }, {});
  const programSellOrders = program_orders.filter(o => o.order_type == 'sell');

  var estimateDetailCols = [
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: 'Fee',
      dataIndex: 'fee_percentage',
      key: 'fee_percentage',
    },
    {
      title: 'Fund Certs Quantity',
      dataIndex: 'fund_cert_quantity',
      key: 'fund_cert_quantity',
    },
    {
      title: 'Fund Certs Amount',
      dataIndex: 'fund_cert_amount',
      key: 'fund_cert_amount',
    },
    {
      title: 'Fee Amount',
      dataIndex: 'fee_amount',
      key: 'fee_amount',
    },
  ];

  var programOrderBuyCols = [
    {
      dataIndex: 'id',
    },
    {
      title: 'Kỳ',
      dataIndex: 'interval',
      key: 'interval',
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
    },
    {
      title: 'Order Date',
      dataIndex: 'order_date',
      key: 'order_date',
    },
    {
      title: 'Product',
      dataIndex: 'product_code',
      key: 'product_code',
    },
    {
      title: 'Matched Price',
      dataIndex: 'matched_price',
      key: 'matched_price',
    },
    {
      title: 'Employee Contribute Amount',
      dataIndex: 'employee_amount',
      key: 'employee_amount',
    },
    {
      title: 'Company Contribute Amount',
      dataIndex: 'company_amount',
      key: 'company_amount',
    },
    {
      title: 'Employee Quantity',
      dataIndex: 'employee_quantity',
      key: 'employee_quantity',
    },
    {
      title: 'Company Quantity',
      dataIndex: 'company_quantity',
      key: 'company_quantity',
    },
    {
      title: 'Order Type',
      dataIndex: 'company_order_type',
      key: 'company_order_type',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
  ];

  let programOrderBuyDataSource = employeeProgramOrders.map((item, index) => ({
    id: (
      <Link to={`/futureflex/program-orders/${item.id}`}>
        <EyeIcon />
      </Link>
    ),
    interval: item.interval,
    note:
      companyProgramOrdersMap[item.interval] &&
      companyProgramOrdersMap[item.interval].status ==
        programOrderStatusComplete ? (
        <Tag color="green">Công ty đóng đủ</Tag>
      ) : (
        <Tag color="red">Công ty đóng thiếu</Tag>
      ),
    order_date: formatDateTime(item.order_date),
    product_code: item.product_code,
    matched_price: new Intl.NumberFormat('en-US').format(item.matched_price),
    employee_amount: new Intl.NumberFormat('en-US').format(item.amount),
    company_amount:
      companyProgramOrdersMap[item.interval] &&
      companyProgramOrdersMap[item.interval].amount
        ? new Intl.NumberFormat('en-US').format(
            companyProgramOrdersMap[item.interval].amount
          )
        : 0,
    employee_quantity: new Intl.NumberFormat('en-US').format(item.quantity),
    company_quantity:
      companyProgramOrdersMap[item.interval] &&
      companyProgramOrdersMap[item.interval].quantity
        ? new Intl.NumberFormat('en-US').format(
            companyProgramOrdersMap[item.interval].quantity
          )
        : 0,
    company_order_type: companyProgramOrdersMap[item.interval] ? (
      <Tag
        color={
          orderTypeColors[companyProgramOrdersMap[item.interval].order_type]
        }
      >
        {companyProgramOrdersMap[item.interval].order_type}
      </Tag>
    ) : (
      ''
    ),
    status: (
      <Tag color={programOrderStatusColors[item.status]}>
        {programOrderStatus[item.status]}
      </Tag>
    ),
  }));

  programOrderBuyDataSource.sort((a, b) => a.interval - b.interval);

  var programOrderSellCols = [
    { dataIndex: 'id' },
    {
      title: 'Interval',
      dataIndex: 'interval',
      key: 'interval',
    },
    {
      title: 'Bên góp',
      dataIndex: 'order_side',
    },
    {
      title: 'Order Date',
      dataIndex: 'order_date',
      key: 'order_date',
    },
    {
      title: 'Product',
      dataIndex: 'product_code',
      key: 'product_code',
    },
    {
      title: 'Matched Price',
      dataIndex: 'matched_price',
      key: 'matched_price',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Order Type',
      dataIndex: 'order_type',
      key: 'order_type',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
  ];

  let programOrderSellDataSource = programSellOrders.map((item, index) => ({
    id: (
      <Link to={`/futureflex/program-orders/${item.id}`}>
        <EyeIcon />
      </Link>
    ),
    interval: item.interval,
    order_side: (
      <Tag color={orderSideColors[item.order_side]}>
        {orderSideLabels[item.order_side]}
      </Tag>
    ),
    order_date: formatDateTime(item.order_date),
    product_code: item.product_code,
    matched_price: new Intl.NumberFormat('en-US').format(item.matched_price),
    employee_amount: new Intl.NumberFormat('en-US').format(item.amount),
    amount: new Intl.NumberFormat('en-US').format(item.amount),
    quantity: new Intl.NumberFormat('en-US').format(item.quantity),
    order_type: (
      <Tag color={orderTypeColors[item.order_type]}>{item.order_type}</Tag>
    ),
    status: (
      <Tag color={programOrderStatusColors[item.status]}>
        {programOrderStatus[item.status]}
      </Tag>
    ),
  }));

  return (
    <>
      <Descriptions
        title="Estimate Vesting"
        column={1}
        extra={
          <Button
            disabled={isSubmittingTriggerCompanySellAllOrders}
            loading={isSubmittingTriggerCompanySellAllOrders}
            type="primary"
            onClick={() =>
              confirm(
                'Confirm to trigger company sell order? This action can not rollback'
              ) && onTriggerCompanySellAllOrders()
            }
          >
            Create All Sell Orders
          </Button>
        }
      ></Descriptions>
      {estimate &&
        estimate.map((estimateItem, index) => (
          <Space direction="vertical" size="large">
            <Descriptions
              className="px-4"
              title={`${estimateItem.fund_code} Estimate Vesting`}
              bordered
              column={1}
            >
              <Descriptions.Item label={'Product'}>
                {estimateItem.fund_code}
              </Descriptions.Item>
              <Descriptions.Item label={'Product NAV'}>
                {new Intl.NumberFormat('en-US').format(estimateItem.fund_nav)}
              </Descriptions.Item>
              <Descriptions.Item label={'Total Fund Certs Quantity'}>
                {new Intl.NumberFormat('en-US').format(
                  estimateItem.fund_cert_quantity
                )}
              </Descriptions.Item>
              <Descriptions.Item label={'Total Fund Certs Amount'}>
                {new Intl.NumberFormat('en-US').format(
                  estimateItem.fund_cert_amount
                )}
              </Descriptions.Item>
              <Descriptions.Item label={'Total Company Unpaid Amount'}>
                {new Intl.NumberFormat('en-US').format(
                  estimateItem.company_unpaid_amount
                )}
              </Descriptions.Item>
              <Descriptions.Item label={'Total Fee'}>
                {new Intl.NumberFormat('en-US').format(estimateItem.fee_amount)}
              </Descriptions.Item>
              <Descriptions.Item label={'Total Amount'}>
                <Tag color="purple">
                  {new Intl.NumberFormat('en-US').format(estimateItem.amount)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={'Action'}>
                <Button
                  disabled={isSubmittingTriggerSellOrder}
                  loading={isSubmittingTriggerSellOrder}
                  type="default"
                  onClick={() =>
                    confirm(
                      'Confirm to trigger company sell order? This action can not rollback'
                    ) && onTriggerCompanySellOrder(estimateItem.product_id)
                  }
                >
                  Create Sell Order for {estimateItem.fund_code}
                </Button>
              </Descriptions.Item>
            </Descriptions>
            <Descriptions title="Detail" column={2}>
              <Descriptions.Item>
                <Table
                  dataSource={estimateItem.items.map((item, index) => ({
                    key: index,
                    label: item.label,
                    fee_percentage: `${item.fee_percentage}%`,
                    fund_cert_quantity: new Intl.NumberFormat('en-US').format(
                      item.fund_cert_quantity
                    ),
                    fund_cert_amount: new Intl.NumberFormat('en-US').format(
                      item.fund_cert_amount
                    ),
                    fee_amount: new Intl.NumberFormat('en-US').format(
                      item.fee_amount
                    ),
                  }))}
                  pagination={false}
                  columns={estimateDetailCols}
                />
              </Descriptions.Item>
            </Descriptions>
          </Space>
        ))}
      <h3 className="text-base font-medium">Program Orders</h3>

      <DataTable
        columns={employeeProgramOrdersColumnsGenerator()}
        data={program_orders}
        tableName="estimateEmployeeProgramOrders"
      />
      <h3 className="text-base font-medium">Program Buy Orders</h3>
      <div className="overflow-x-scroll">
        <Table
          dataSource={programOrderBuyDataSource}
          pagination={false}
          columns={programOrderBuyCols}
        />
      </div>
      <h3 className="text-base font-medium">Program Sell Orders</h3>
      <div className="overflow-x-scroll">
        <Table
          dataSource={programOrderSellDataSource}
          pagination={false}
          columns={programOrderSellCols}
        />
      </div>
    </>
  );
};

export default EstimateVesting;
